/* eslint-disable @typescript-eslint/no-explicit-any */
import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { ROLE_CACHE_GC_MS, ROLE_CACHE_STALE_MS } from "@/lib/queryClient";
import { rolePipelineLite } from "@/config/endpoints";

/**
 * Wire-trim variant of useRolesPipeline. Use this on lookup-only callers
 * (lead filter dropdowns, assign panels) where only `{ id, name, users:
 * [{ id, name, roleId }] }` is needed. Skips the per-role permissions
 * blob and the user branches/customFieldAccess includes — typical
 * payload drops from ~300 KB to ~30 KB on a populous tenant.
 */
export default function useRolesPipelineLite(
  filters: Record<string, any> = {},
  opts: { enabled?: boolean } = {}
) {
  const query = useQuery({
    queryKey: ["roles-pipeline", "lite", filters],
    queryFn: async () => {
      const res = await axiosInstance.get(rolePipelineLite, { params: filters });
      return res.data.rolePipeline;
    },
    // Role shape changes rarely and every writer that changes it already
    // busts this key (role save/delete in FormRoleContext, invalidateUserCaches,
    // invalidateBranchCaches, invalidateLeadAssignment,
    // invalidatePipelineDependentCaches, USER_ACCESS_REFRESH). Long stale /
    // gc windows so remounts across pages don't refetch it — it was 175k
    // GETs a day under the 3-min default plus the socket bundle's prefix bust.
    staleTime: ROLE_CACHE_STALE_MS,
    gcTime: ROLE_CACHE_GC_MS,
    // Callers that only need the list conditionally (e.g. the drawer's
    // slot picker, managers only) can hold the fetch entirely.
    enabled: opts.enabled ?? true,
  });

  const rolesSelects =
    query.data?.map((val: any) => ({
      value: String(val.id),
      name: val.name,
    })) ?? [];

  return {
    rolesPipeline: query.data,
    length: query.data ? query.data.length : 0,
    rolesSelects,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
